import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import Table from "react-bootstrap/esm/Table";
import Spinner from "react-bootstrap/esm/Spinner";
import Alert from "react-bootstrap/esm/Alert";
import Collapse from "react-bootstrap/esm/Collapse";
import { httpsCallable } from "firebase/functions";
import { collection, getDocs } from "firebase/firestore";
import { db, functions } from "../services/firebase";
import { auth } from "../services/firebase";
import Preacher, { preacherConverter } from "../DTOs/Preacher";
import PlannedSermon, { ServiceType, SlotMode } from "../DTOs/PlannedSermon";
import Holiday from "../DTOs/Holiday";
import { Globals } from "../globals";
import {
    saveSundayServiceFromPlannedSermon,
    getExistingPlanFields,
    updateSundayServicePlanFields,
    getPlannedSundayServices,
    saveHoliday,
    getHolidays,
    deleteItemFromFirestore,
} from "../services/firestoreService";
import { handleFirebaseError } from "../utils/errorHandler";
import I_IMC_Props from "../infos/I_IMC_Props";
import { useAuth } from "../hooks/useAuth";
import { loadAbsencesByPreacher } from "../services/absenceService";
import { AbsenceMap, findAbsence, formatAbsence } from "../utils/absences";
import { useGemeinde } from "../context/GemeindeContext";
import { gemeindeSubPath } from "../services/gemeindePaths";
import { diffPlanFields, PlanFields } from "../utils/sermonPlanDiff";
import { selectablePreachers } from "../utils/preachers";
import ImportCodesEditor from "./ImportCodesEditor";

interface ParsedPlannedService {
    date: string;
    weekday: string;
    serviceType: ServiceType;
    serviceLabel: string;
    sermon1PreacherName: string;
    sermon2PreacherName: string;
    sermon1Mode: SlotMode;
    sermon2Mode: SlotMode;
    verkuendiger: string;
    guestPreacherOrigin: string;
    afternoonEvent: string;
    choirInfo: string;
    serviceNote: string;
    sourceSlots: number[];
}

interface ParsedHoliday {
    date: string;
    name: string;
    kuerzel: string;
    weekday: string;
    isPublicHoliday: boolean;
}

interface ApprovalEntry {
    key: string;
    parsed: ParsedPlannedService;
    sermon1PreacherKey: string;
    sermon2PreacherKey: string;
    included: boolean;
    // The plan fields of the service this row already exists as (re-import), or
    // null for a new service. For existing services `included` means "apply the changes".
    existing: PlanFields | null;
}

/** For a re-imported row: the plan fields that differ from the stored service. */
function entryChanges(entry: ApprovalEntry): Partial<PlanFields> {
    if (!entry.existing) return {};
    return diffPlanFields(entry.existing, {
        sermon1PreacherKey: entry.sermon1PreacherKey,
        sermon2PreacherKey: entry.sermon2PreacherKey,
        sermon1Mode: entry.parsed.sermon1Mode,
        sermon2Mode: entry.parsed.sermon2Mode,
        serviceLabel: entry.parsed.serviceLabel,
    });
}

interface HolidayEntry {
    key: string;
    parsed: ParsedHoliday;
    included: boolean;
}

interface SavedEntry {
    docId: string;
    date: string;
    weekday: string;
    serviceType: ServiceType;
    serviceLabel: string;
    sermon1Mode: SlotMode;
    sermon2Mode: SlotMode;
    sermon1PreacherKey: string;
    sermon2PreacherKey: string;
}

interface SavedHoliday {
    docId: string;
    date: string;
    name: string;
    kuerzel: string;
    weekday: string;
    isPublicHoliday: boolean;
}

const SLOT_MODE_LABEL: Record<SlotMode, string> = {
    sermon: "Predigt",
    intro: "Gebetsgemeinschaft",
    reminder: "Erinnerung",
};

function matchPreacherKey(fullName: string, preachers: Preacher[]): string {
    if (!fullName) return "";
    const parts = fullName.trim().split(/\s+/);
    if (parts.length < 2) return "";
    const firstName = parts[0];
    const lastName = parts.slice(1).join(" ");
    const found = preachers.find(
        (p) =>
            p.name.toLowerCase() === firstName.toLowerCase() &&
            p.surname.toLowerCase() === lastName.toLowerCase()
    );
    return found?.key ?? "";
}

export default function SermonPlanImport({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t, i18n } = useTranslation();
    const { isAuthenticated, loading: authLoading } = useAuth();
    const { activeGemeindeId } = useGemeinde();

    const [preachers, setPreachers] = useState<Preacher[]>([]);
    const [absences, setAbsences] = useState<AbsenceMap>(new Map());
    const [sheetName, setSheetName] = useState("");
    // Format help and the Gemeinde's import codes (GOD-48), collapsed by default.
    const [openPanel, setOpenPanel] = useState<"format" | "codes" | null>(null);
    const [parsing, setParsing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [entries, setEntries] = useState<ApprovalEntry[]>([]);
    const [showEntries, setShowEntries] = useState(false);
    const [holidayEntries, setHolidayEntries] = useState<HolidayEntry[]>([]);
    const [savingHolidays, setSavingHolidays] = useState(false);
    const [savedEntries, setSavedEntries] = useState<SavedEntry[]>([]);
    const [savedHolidays, setSavedHolidays] = useState<SavedHoliday[]>([]);
    const [loadingSaved, setLoadingSaved] = useState(false);

    const getDateLocale = () => (i18n.language === "de" ? "de-DE" : "en-US");

    const formatDate = (dateStr: string): string => {
        try {
            return new Date(dateStr + "T00:00:00").toLocaleDateString(getDateLocale(), {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        } catch {
            return dateStr;
        }
    };

    const getPreacherNameByKey = (preacherKey: string): string => {
        if (!preacherKey) return "";
        const preacher = preachers.find((p) => p.key === preacherKey);
        return preacher ? `${preacher.name} ${preacher.surname}` : t("common.deletedPreacher");
    };

    const loadPreachers = useCallback(async () => {
        if (!activeGemeindeId) return;
        try {
            const snapshot = await getDocs(
                collection(db, ...gemeindeSubPath(activeGemeindeId, Globals.PREACHER_SUBCOLLECTION)).withConverter(preacherConverter)
            );
            const list: Preacher[] = [];
            snapshot.forEach((d) => list.push(d.data()));
            list.sort((a, b) => (a.name + a.surname).localeCompare(b.name + b.surname));
            setPreachers(list);
            setAbsences(await loadAbsencesByPreacher(activeGemeindeId, list.map((p) => p.key)));
        } catch (error) {
            handleFirebaseError(error, "loadPreachers");
        }
    }, [activeGemeindeId]);

    const loadSavedEntries = useCallback(async () => {
        if (!activeGemeindeId) return;
        setLoadingSaved(true);
        try {
            const [services, holidays] = await Promise.all([
                getPlannedSundayServices(activeGemeindeId),
                getHolidays(activeGemeindeId),
            ]);
            setSavedEntries(
                services.map((s) => ({
                    docId: s.docId,
                    date: `${s.date.getFullYear()}-${String(s.date.getMonth() + 1).padStart(2, "0")}-${String(s.date.getDate()).padStart(2, "0")}`,
                    weekday: s.weekday || "",
                    serviceType: (s.serviceType || "manual") as ServiceType,
                    serviceLabel: s.serviceLabel || "Gottesdienst",
                    sermon1Mode: (s.sermon1Mode || "sermon") as SlotMode,
                    sermon2Mode: (s.sermon2Mode || "sermon") as SlotMode,
                    sermon1PreacherKey: s.sermon1PreacherKey ?? "",
                    sermon2PreacherKey: s.sermon2PreacherKey ?? "",
                }))
            );
            setSavedHolidays(
                holidays.map((h) => ({
                    docId: h.docId,
                    date: h.date,
                    name: h.name,
                    kuerzel: h.kuerzel,
                    weekday: h.weekday,
                    isPublicHoliday: h.isPublicHoliday,
                }))
            );
        } catch (error) {
            setVariant("danger");
            setMessage(handleFirebaseError(error, "getPlannedSundayServices"));
            setShowMessage(true);
        }
        setLoadingSaved(false);
    }, [setVariant, setMessage, setShowMessage, activeGemeindeId]);

    useEffect(() => {
        if (authLoading || !isAuthenticated) return;
        loadPreachers();
        loadSavedEntries();
    }, [isAuthenticated, authLoading, loadPreachers, loadSavedEntries]);

    async function handleParseExcel(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const fileInput = e.currentTarget.elements.namedItem("excelFile") as HTMLInputElement;

        if (!fileInput?.files?.length) {
            setVariant("warning");
            setMessage(t("admin.selectExcelFile") as string);
            setShowMessage(true);
            return;
        }
        if (!sheetName.trim()) {
            setVariant("warning");
            setMessage(t("admin.enterSheetName") as string);
            setShowMessage(true);
            return;
        }
        if (!activeGemeindeId) return;

        setParsing(true);
        setShowEntries(false);
        setEntries([]);
        setHolidayEntries([]);

        try {
            const file = fileInput.files[0];
            const xlsxBase64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result;
                    if (typeof result !== "string") {
                        reject(new Error("Unexpected FileReader result type"));
                        return;
                    }
                    const commaIndex = result.indexOf(",");
                    resolve(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);
                };
                reader.onerror = () => {
                    reject(reader.error ?? new Error(t("admin.excelReadError") as string));
                };
                reader.readAsDataURL(file);
            });

            const parseSermonPlanFn = httpsCallable<
                { xlsxBase64: string; sheetName: string; gemeindeId: string },
                { services: ParsedPlannedService[]; holidays: ParsedHoliday[] }
            >(functions, "parseSermonPlan");

            const result = await parseSermonPlanFn({ xlsxBase64, sheetName: sheetName.trim(), gemeindeId: activeGemeindeId });
            const parsed = result.data.services;
            const parsedHolidays = result.data.holidays ?? [];

            setHolidayEntries(
                parsedHolidays.map((h, idx) => ({
                    key: `${h.date}_${h.kuerzel || "x"}_${idx}`,
                    parsed: h,
                    included: true,
                }))
            );

            if (parsed.length === 0 && parsedHolidays.length === 0) {
                setVariant("warning");
                setMessage(t("admin.noServicesExtractedExcel") as string);
                setShowMessage(true);
                setParsing(false);
                return;
            }

            // Re-import: look up which rows already exist as a service. Those are
            // not saved again; the admin decides per row whether to apply the changes.
            const existingFields = await Promise.all(
                parsed.map((p) => getExistingPlanFields(activeGemeindeId, p.date, p.serviceType))
            );
            const approvalEntries: ApprovalEntry[] = parsed.map((p, idx) => ({
                key: `${p.date}_${p.serviceType}_${p.sourceSlots.join("-") || "none"}_${idx}`,
                parsed: p,
                sermon1PreacherKey: matchPreacherKey(p.sermon1PreacherName, selectablePreachers(preachers)),
                sermon2PreacherKey: matchPreacherKey(p.sermon2PreacherName, selectablePreachers(preachers)),
                included: existingFields[idx] === null,
                existing: existingFields[idx],
            }));

            setEntries(approvalEntries);
            setShowEntries(true);
        } catch (error) {
            setVariant("danger");
            setMessage(`${t("admin.excelParseError")} ${error instanceof Error ? error.message : ""}`);
            setShowMessage(true);
        }

        setParsing(false);
    }

    async function handleSaveApproved() {
        // Existing services are only saved when they actually have changes to apply.
        const toSave = entries.filter((e) => e.included && (!e.existing || Object.keys(entryChanges(e)).length > 0));
        if (toSave.length === 0) return;
        if (!activeGemeindeId) return;

        setSaving(true);
        try {
            const userId = auth.currentUser?.uid ?? "";
            const now = new Date();

            await Promise.all(
                toSave.map((entry) => {
                    if (entry.existing) {
                        // Only the changed plan fields; what preachers entered stays.
                        return updateSundayServicePlanFields(
                            activeGemeindeId,
                            entry.parsed.date,
                            entry.parsed.serviceType,
                            entryChanges(entry)
                        );
                    }
                    const plannedSermon = new PlannedSermon(
                        entry.parsed.date,
                        entry.parsed.weekday,
                        entry.parsed.serviceType,
                        entry.parsed.serviceLabel,
                        entry.parsed.sermon1PreacherName,
                        entry.sermon1PreacherKey,
                        entry.parsed.sermon1Mode,
                        entry.parsed.sermon2PreacherName,
                        entry.sermon2PreacherKey,
                        entry.parsed.sermon2Mode,
                        userId,
                        now,
                        entry.parsed.verkuendiger,
                        entry.parsed.guestPreacherOrigin,
                        entry.parsed.afternoonEvent,
                        entry.parsed.choirInfo,
                        entry.parsed.serviceNote
                    );
                    return saveSundayServiceFromPlannedSermon(activeGemeindeId, plannedSermon);
                })
            );

            setVariant("success");
            setMessage(t("admin.plannedServicesSaved", { count: toSave.length }) as string);
            setShowMessage(true);
            setEntries([]);
            setShowEntries(false);
            await loadSavedEntries();
        } catch (error) {
            setVariant("danger");
            setMessage(handleFirebaseError(error, "savePlannedServices"));
            setShowMessage(true);
        }
        setSaving(false);
    }

    async function handleSaveHolidays() {
        const toSave = holidayEntries.filter((h) => h.included);
        if (toSave.length === 0) return;
        if (!activeGemeindeId) return;

        setSavingHolidays(true);
        try {
            const userId = auth.currentUser?.uid ?? "";
            const now = new Date();
            await Promise.all(
                toSave.map((entry) =>
                    saveHoliday(
                        activeGemeindeId,
                        new Holiday(
                            entry.parsed.date,
                            entry.parsed.name,
                            entry.parsed.kuerzel,
                            entry.parsed.weekday,
                            entry.parsed.isPublicHoliday,
                            userId,
                            now
                        )
                    )
                )
            );

            setVariant("success");
            setMessage(t("admin.holidaysSaved", { count: toSave.length }) as string);
            setShowMessage(true);
            setHolidayEntries([]);
            await loadSavedEntries();
        } catch (error) {
            setVariant("danger");
            setMessage(handleFirebaseError(error, "saveHolidays"));
            setShowMessage(true);
        }
        setSavingHolidays(false);
    }

    async function handleDeleteSaved(docId: string) {
        if (!activeGemeindeId) return;
        if (!window.confirm(t("admin.deleteConfirm") as string)) return;
        try {
            await deleteItemFromFirestore(activeGemeindeId, Globals.SUNDAY_SERVICE_SUBCOLLECTION, docId);
            setVariant("success");
            setMessage(t("admin.plannedServiceDeleted") as string);
            setShowMessage(true);
            await loadSavedEntries();
        } catch (error) {
            setVariant("danger");
            setMessage(handleFirebaseError(error, "deletePlannedSundayService"));
            setShowMessage(true);
        }
    }

    async function handleDeleteHoliday(docId: string) {
        if (!activeGemeindeId) return;
        if (!window.confirm(t("admin.deleteConfirm") as string)) return;
        try {
            await deleteItemFromFirestore(activeGemeindeId, Globals.FEIERTAGE_SUBCOLLECTION, docId);
            setVariant("success");
            setMessage(t("admin.holidayDeleted") as string);
            setShowMessage(true);
            await loadSavedEntries();
        } catch (error) {
            setVariant("danger");
            setMessage(handleFirebaseError(error, "deleteHoliday"));
            setShowMessage(true);
        }
    }

    function toggleIncluded(idx: number) {
        setEntries((prev) =>
            prev.map((e, i) => (i === idx ? { ...e, included: !e.included } : e))
        );
    }

    function setEntryPreacherKey(idx: number, slot: 1 | 2, key: string) {
        setEntries((prev) =>
            prev.map((e, i) =>
                i === idx
                    ? { ...e, [slot === 1 ? "sermon1PreacherKey" : "sermon2PreacherKey"]: key }
                    : e
            )
        );
    }

    function toggleHolidayIncluded(idx: number) {
        setHolidayEntries((prev) =>
            prev.map((h, i) => (i === idx ? { ...h, included: !h.included } : h))
        );
    }

    const checkedCount = entries.filter(
        (e) => e.included && (!e.existing || Object.keys(entryChanges(e)).length > 0)
    ).length;

    const preacherName = (key: string) => {
        if (!key) return "–";
        const preacher = preachers.find((p) => p.key === key);
        return preacher ? `${preacher.name} ${preacher.surname}` : t("common.deletedPreacher");
    };

    // What a re-import would change on an existing service, as "label: old → new" lines.
    const renderChanges = (entry: ApprovalEntry) => {
        const changes = entryChanges(entry);
        const existing = entry.existing!;
        const lines: Array<[string, string, string]> = [];
        if (changes.sermon1PreacherKey !== undefined)
            lines.push([t("admin.preacher1"), preacherName(existing.sermon1PreacherKey), preacherName(changes.sermon1PreacherKey)]);
        if (changes.sermon2PreacherKey !== undefined)
            lines.push([t("admin.preacher2"), preacherName(existing.sermon2PreacherKey), preacherName(changes.sermon2PreacherKey)]);
        if (changes.sermon1Mode !== undefined)
            lines.push([`${t("admin.slotMode")} 1`, SLOT_MODE_LABEL[existing.sermon1Mode], SLOT_MODE_LABEL[changes.sermon1Mode]]);
        if (changes.sermon2Mode !== undefined)
            lines.push([`${t("admin.slotMode")} 2`, SLOT_MODE_LABEL[existing.sermon2Mode], SLOT_MODE_LABEL[changes.sermon2Mode]]);
        if (changes.serviceLabel !== undefined)
            lines.push([t("admin.serviceLabel"), existing.serviceLabel || "–", changes.serviceLabel || "–"]);
        if (lines.length === 0) return <span className="text-muted">{t("admin.reimportUnchanged")}</span>;
        return (
            <>
                <span className="gl-badge gl-badge--warn">{t("admin.reimportChanged")}</span>
                {lines.map(([label, from, to]) => (
                    <div key={label} className="small mt-1">
                        {label}: <s className="text-muted">{from}</s> → <strong>{to}</strong>
                    </div>
                ))}
            </>
        );
    };
    const checkedHolidayCount = holidayEntries.filter((h) => h.included).length;

    const renderPreacherSelect = (idx: number, slot: 1 | 2, currentKey: string) => (
        <Form.Select
            size="sm"
            className="mt-1"
            value={currentKey}
            onChange={(e) => setEntryPreacherKey(idx, slot, e.target.value)}
            aria-label={t(slot === 1 ? "admin.preacher1" : "admin.preacher2") as string}
            isInvalid={!currentKey}
        >
            <option value="">{t("admin.selectPreacher")}</option>
            {selectablePreachers(preachers, currentKey).map((p) => (
                <option key={p.key} value={p.key}>
                    {p.name} {p.surname}
                </option>
            ))}
        </Form.Select>
    );

    // Shown under a preacher select when that preacher is absent on the service date.
    const renderAbsenceWarning = (preacherKey: string, date: string) => {
        const absence = findAbsence(absences, preacherKey, date);
        if (!absence) return null;
        return (
            <small className="text-danger d-block mt-1">
                ⚠ {t("absences.importWarning", { period: formatAbsence(absence, t("calendar.locale") as string) })}
            </small>
        );
    };

    const renderServiceDetails = (p: ParsedPlannedService) => {
        const items: string[] = [];
        if (p.verkuendiger) items.push(`${t("admin.verkuendiger")}: ${p.verkuendiger}`);
        if (p.guestPreacherOrigin) items.push(`${t("admin.guestPreacher")}: ${p.guestPreacherOrigin}`);
        if (p.afternoonEvent) items.push(`${t("admin.afternoonEvent")}: ${p.afternoonEvent}`);
        if (p.choirInfo) items.push(`${t("admin.choirInfo")}: ${p.choirInfo}`);
        if (p.serviceNote) items.push(`${t("admin.serviceNote")}: ${p.serviceNote}`);
        if (items.length === 0) return <span className="text-muted">–</span>;
        return (
            <small className="text-muted">
                {items.map((item, i) => (
                    <div key={i}>{item}</div>
                ))}
            </small>
        );
    };

    return (
        <div className="mx-auto" style={{ maxWidth: "1100px" }}>
            <h2>{t("admin.sermonPlanImportHeading")}</h2>

            {/* Template, file format and the Gemeinde's codes (GOD-48) */}
            <div className="d-flex flex-wrap gap-2 my-3">
                <Button
                    as="a"
                    variant="outline-primary"
                    size="sm"
                    href="/vorlagen/predigtplan-vorlage.xlsx"
                    download
                    data-testid="download-template"
                >
                    {t("admin.importFormat.download")}
                </Button>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    aria-expanded={openPanel === "format"}
                    onClick={() => setOpenPanel(openPanel === "format" ? null : "format")}
                >
                    {t("admin.importFormat.toggle")}
                </Button>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    aria-expanded={openPanel === "codes"}
                    onClick={() => setOpenPanel(openPanel === "codes" ? null : "codes")}
                    data-testid="toggle-import-codes"
                >
                    {t("admin.importCodes.toggle")}
                </Button>
            </div>
            <Collapse in={openPanel === "format"}>
                <div>
                    <div className="border rounded p-3 mb-3">
                        <p className="mb-2">{t("admin.importFormat.intro")}</p>
                        <ul className="mb-0">
                            {(t("admin.importFormat.rules", { returnObjects: true }) as string[]).map((rule) => (
                                <li key={rule}>{rule}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Collapse>
            <Collapse in={openPanel === "codes"} mountOnEnter>
                <div>
                    <div className="border rounded p-3 mb-3">
                        {activeGemeindeId && (
                            <ImportCodesEditor
                                gemeindeId={activeGemeindeId}
                                setVariant={setVariant}
                                setMessage={setMessage}
                                setShowMessage={setShowMessage}
                            />
                        )}
                    </div>
                </div>
            </Collapse>

            {/* Upload and parse form */}
            <Form onSubmit={handleParseExcel} id="SermonPlanImportForm">
                <Form.Group className="mb-3" controlId="SermonPlanImportForm.excelFile">
                    <Form.Label>{t("admin.sermonPlanExcelLabel")}</Form.Label>
                    <Form.Control type="file" name="excelFile" accept=".xlsx,.xls" required />
                    <Form.Text className="text-muted">{t("admin.sermonPlanExcelHelp")}</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="SermonPlanImportForm.sheetName">
                    <Form.Label>{t("admin.sermonPlanSheetLabel")}</Form.Label>
                    <Form.Control
                        type="text"
                        value={sheetName}
                        onChange={(e) => setSheetName(e.target.value)}
                        placeholder="GS Q2-26 Se"
                        required
                    />
                    <Form.Text className="text-muted">{t("admin.sermonPlanSheetHelp")}</Form.Text>
                </Form.Group>
                <Form.Group controlId="SermonPlanImportForm.Submit">
                    <Button className="shadow-sm" type="submit" disabled={parsing}>
                        {parsing ? (
                            <>
                                {t("admin.parsing")}{" "}
                                <Spinner size="sm" animation="grow" as="span" />
                            </>
                        ) : (
                            t("admin.parseExcel")
                        )}
                    </Button>
                </Form.Group>
            </Form>

            {/* Extracted entries approval table */}
            {showEntries && entries.length > 0 && (
                <>
                    <br />
                    <h4>{t("admin.extractedServices")}</h4>
                    <Alert variant="info">{t("admin.extractedServicesHelp")}</Alert>
                    {entries.some((e) => e.existing) && <Alert variant="warning">{t("admin.reimportHelp")}</Alert>}
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>{t("admin.includeEntry")}</th>
                                <th>{t("admin.reimportStatus")}</th>
                                <th>{t("sermon.date")}</th>
                                <th>{t("admin.serviceType")}</th>
                                <th>{t("admin.preacher1")}</th>
                                <th>{t("admin.preacher2")}</th>
                                <th>{t("admin.serviceDetails")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, idx) => (
                                <tr key={entry.key} className={entry.included ? "" : "table-secondary"}>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            checked={entry.included && (!entry.existing || Object.keys(entryChanges(entry)).length > 0)}
                                            disabled={!!entry.existing && Object.keys(entryChanges(entry)).length === 0}
                                            onChange={() => toggleIncluded(idx)}
                                            aria-label={t(entry.existing ? "admin.reimportApply" : "admin.includeEntry") as string}
                                        />
                                    </td>
                                    <td>
                                        {entry.existing ? (
                                            renderChanges(entry)
                                        ) : (
                                            <span className="gl-badge gl-badge--ok">{t("admin.reimportNew")}</span>
                                        )}
                                    </td>
                                    <td>{formatDate(entry.parsed.date)}</td>
                                    <td>
                                        <div className="fw-semibold">{entry.parsed.serviceLabel || entry.parsed.serviceType}</div>
                                        <small className="text-muted">{entry.parsed.weekday}</small>
                                    </td>
                                    <td>
                                        {entry.parsed.sermon1PreacherName ? (
                                            <>
                                                <div>{entry.parsed.sermon1PreacherName}</div>
                                                <small className="text-muted d-block">{SLOT_MODE_LABEL[entry.parsed.sermon1Mode]}</small>
                                                {renderPreacherSelect(idx, 1, entry.sermon1PreacherKey)}
                                                {renderAbsenceWarning(entry.sermon1PreacherKey, entry.parsed.date)}
                                            </>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td>
                                        {entry.parsed.sermon2PreacherName ? (
                                            <>
                                                <div>{entry.parsed.sermon2PreacherName}</div>
                                                <small className="text-muted d-block">{SLOT_MODE_LABEL[entry.parsed.sermon2Mode]}</small>
                                                {renderPreacherSelect(idx, 2, entry.sermon2PreacherKey)}
                                                {renderAbsenceWarning(entry.sermon2PreacherKey, entry.parsed.date)}
                                            </>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td>{renderServiceDetails(entry.parsed)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Button
                        variant="primary"
                        onClick={handleSaveApproved}
                        disabled={saving || checkedCount === 0}
                        className="shadow-sm"
                    >
                        {saving ? (
                            <>
                                {t("admin.saving")}{" "}
                                <Spinner size="sm" animation="grow" as="span" />
                            </>
                        ) : (
                            `${t("admin.approveSelected")} (${checkedCount})`
                        )}
                    </Button>
                </>
            )}

            {/* Extracted holidays approval table */}
            {holidayEntries.length > 0 && (
                <>
                    <br />
                    <h4>{t("admin.holidaysExtracted")}</h4>
                    <Alert variant="info">{t("admin.holidaysHelp")}</Alert>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>{t("admin.includeEntry")}</th>
                                <th>{t("sermon.date")}</th>
                                <th>{t("admin.holidayName")}</th>
                                <th>{t("admin.kuerzel")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {holidayEntries.map((entry, idx) => (
                                <tr key={entry.key} className={entry.included ? "" : "table-secondary"}>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            checked={entry.included}
                                            onChange={() => toggleHolidayIncluded(idx)}
                                            aria-label={t("admin.includeEntry") as string}
                                        />
                                    </td>
                                    <td>{formatDate(entry.parsed.date)}</td>
                                    <td>{entry.parsed.name}</td>
                                    <td>{entry.parsed.kuerzel || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Button
                        variant="primary"
                        onClick={handleSaveHolidays}
                        disabled={savingHolidays || checkedHolidayCount === 0}
                        className="shadow-sm"
                    >
                        {savingHolidays ? (
                            <>
                                {t("admin.saving")}{" "}
                                <Spinner size="sm" animation="grow" as="span" />
                            </>
                        ) : (
                            `${t("admin.importHolidays")} (${checkedHolidayCount})`
                        )}
                    </Button>
                </>
            )}

            <br />
            <hr />
            <br />

            {/* Saved planned services list */}
            <h4>{t("admin.plannedServicesHeading")}</h4>
            {loadingSaved && <Spinner animation="border" />}
            {!loadingSaved && savedEntries.length === 0 && (
                <p className="text-muted">{t("admin.noPlannedServices")}</p>
            )}
            {savedEntries.length > 0 && (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>{t("sermon.date")}</th>
                            <th>{t("admin.serviceType")}</th>
                            <th>{t("admin.preacher1")}</th>
                            <th>{t("admin.preacher2")}</th>
                            <th>{t("admin.actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {savedEntries.map((entry) => (
                            <tr key={entry.docId}>
                                <td>{formatDate(entry.date)}</td>
                                <td>
                                    <div className="fw-semibold">{entry.serviceLabel || entry.serviceType}</div>
                                    <small className="text-muted">{entry.weekday}</small>
                                </td>
                                <td>{getPreacherNameByKey(entry.sermon1PreacherKey) || "-"}</td>
                                <td>{getPreacherNameByKey(entry.sermon2PreacherKey) || "-"}</td>
                                <td>
                                    <Button
                                        size="sm"
                                        variant="danger"
                                        onClick={() => handleDeleteSaved(entry.docId)}
                                    >
                                        {t("admin.delete")}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <br />

            {/* Saved holidays list */}
            <h4>{t("admin.savedHolidaysHeading")}</h4>
            {!loadingSaved && savedHolidays.length === 0 && (
                <p className="text-muted">{t("admin.noHolidays")}</p>
            )}
            {savedHolidays.length > 0 && (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>{t("sermon.date")}</th>
                            <th>{t("admin.holidayName")}</th>
                            <th>{t("admin.kuerzel")}</th>
                            <th>{t("admin.actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {savedHolidays.map((entry) => (
                            <tr key={entry.docId}>
                                <td>{formatDate(entry.date)}</td>
                                <td>{entry.name}</td>
                                <td>{entry.kuerzel || "-"}</td>
                                <td>
                                    <Button
                                        size="sm"
                                        variant="danger"
                                        onClick={() => handleDeleteHoliday(entry.docId)}
                                    >
                                        {t("admin.delete")}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
